import Debug from "debug";
import { ExperimentEngine } from "@bookingcom/experiment-tracking-clientside";
import * as watchEt from "./watch-et.js";
const debug = Debug("utils:etV2");
const stageTrackedExperiments = new Set();
const loggedExperimentsWithoutStage = new Set();
let getAsyncRequestContext;
if (process.env.BUILD_TARGET === "server") {
    (async () => {
        const { getAsyncRequestContext: getContext } = await import("@flights/turbine");
        getAsyncRequestContext = getContext;
    })().catch(() => {
        throw new Error("Error while importing @flights/turbine");
    });
}
const etV2Fallback = {
    track: () => 0,
    stage: () => false,
    customGoal: () => false,
    goal: () => false,
    goalWithValue: () => false,
    updateStateBlobAndVariants: () => { },
    sinkData: () => { },
    trackWithDefaultStage: () => 0
};
const getExperimentEngineInstance = () => {
    if (process.env.BUILD_TARGET === "client") {
        return ExperimentEngine.getInstance({
            sinkUrl: `${window?.__GLOBAL_CONTEXT__?.apiPrefix || ""}/track/sink`,
            clientsidePayload: (typeof window !== "undefined" && window?.__GLOBAL_CONTEXT__?.clientsidePayload) || "",
            sinkTimeoutMs: 500
        });
    }
    else {
        return etV2Fallback;
    }
};
export const track = (hashedTagname) => {
    if (process.env.BUILD_TARGET === "server") {
        return getAsyncRequestContext()?.request.et?.trackExperiment(hashedTagname) || 0;
    }
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking experiment", hashedTagname);
    watchEt.dispatchName(hashedTagname);
    setTimeout(() => {
        if (!stageTrackedExperiments.has(hashedTagname) && !loggedExperimentsWithoutStage.has(hashedTagname)) {
            loggedExperimentsWithoutStage.add(hashedTagname);
        }
    }, 100);
    return experimentEngineInstance.track(hashedTagname);
};
export const stage = (hashedTagname, id) => {
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking stage", id, "for experiment", hashedTagname);
    watchEt.log([
        {
            type: "stage",
            name: hashedTagname,
            value: id
        }
    ]);
    stageTrackedExperiments.add(hashedTagname);
    return experimentEngineInstance.stage(hashedTagname, id);
};
export const trackWithDefaultStage = (hashedTagname, defaultId) => {
    if (process.env.BUILD_TARGET === "server") {
        const et = getAsyncRequestContext()?.request.et;
        et?.trackExperimentStage(hashedTagname, defaultId);
        return et?.trackExperiment(hashedTagname) || 0;
    }
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking experiment", hashedTagname, " with default stage ", defaultId);
    watchEt.log([
        {
            type: "stage",
            name: hashedTagname,
            value: defaultId
        }
    ]);
    experimentEngineInstance.stage(hashedTagname, defaultId);
    watchEt.dispatchName(hashedTagname);
    return experimentEngineInstance.track(hashedTagname);
};
export const customGoal = (hashedTagname, goal) => {
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking custom goal", goal, "for experiment", hashedTagname);
    watchEt.log([
        {
            type: "customGoal",
            name: hashedTagname,
            value: goal
        }
    ]);
    return experimentEngineInstance.customGoal(hashedTagname, goal);
};
export const goal = (goalName) => {
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking goal", goalName);
    watchEt.log([
        {
            type: "goal",
            name: goalName
        }
    ]);
    return experimentEngineInstance.goal(goalName);
};
export const goalWithValue = (goalName, value) => {
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Tracking goal with value", goalName, "with value", value);
    watchEt.log([
        {
            type: "goalWithValue",
            name: goalName,
            value
        }
    ]);
    return experimentEngineInstance.goalWithValue(goalName, value);
};
export const updateStateBlobAndVariants = (clientsidePayload) => {
    const experimentEngineInstance = getExperimentEngineInstance();
    debug("Updating state blob and variants with clientside payload", clientsidePayload);
    return experimentEngineInstance.updateStateBlobAndVariants(clientsidePayload);
};
export const sinkData = () => {
    const experimentEngineInstance = getExperimentEngineInstance();
    return experimentEngineInstance.sinkData();
};
